import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Activity, Save, Plus, Trash2 } from 'lucide-react';
import { apiClient } from '@/lib/api';

interface SystemStatusData {
  uptime_percentage: number;
  avg_response_time_ms: number;
  sms_delivery_rate: number;
  active_incidents: number;
  services: ServiceStatus[];
  recent_updates: StatusUpdate[];
}

interface ServiceStatus {
  name: string;
  description: string;
  status: 'operational' | 'degraded' | 'outage';
}

interface StatusUpdate {
  id: string;
  date: string;
  title: string;
  description: string;
}

const defaultServices: ServiceStatus[] = [
  { name: 'SMS Messaging', description: 'SMS notifications and two-way messaging', status: 'operational' },
  { name: 'Queue Processing', description: 'Real-time queue management and operations', status: 'operational' },
  { name: 'Web Services', description: 'Admin portal and API access', status: 'operational' },
  { name: 'Database', description: 'Data storage and persistence', status: 'operational' },
  { name: 'User Authentication', description: 'Secure login and account access', status: 'operational' },
  { name: 'AI Assistant', description: 'Smart SMS responses for Premium customers', status: 'operational' }
];

export function SystemStatus() {
  const [statusData, setStatusData] = useState<SystemStatusData>({
    uptime_percentage: 99.9,
    avg_response_time_ms: 180,
    sms_delivery_rate: 99.8,
    active_incidents: 0,
    services: defaultServices,
    recent_updates: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newUpdate, setNewUpdate] = useState({ title: '', description: '' });

  useEffect(() => {
    loadStatusData();
  }, []);

  const loadStatusData = async () => {
    try {
      const response = await apiClient.getSystemStatus();
      setStatusData(response);
    } catch (error) {
      console.error('Failed to load system status:', error);
      // Use default data if API fails - this is expected until backend is deployed
      console.log('Using default status data until backend is implemented');
    } finally {
      setLoading(false);
    }
  };

  const saveStatusData = async () => {
    setSaving(true);
    try {
      await apiClient.updateSystemStatus(statusData);
      alert('System status updated successfully');
    } catch (error) {
      console.error('Failed to save system status:', error);
      alert('Backend not implemented yet. Status changes saved locally only.');
      // For now, just show success since backend isn't deployed
      console.log('Status data that would be saved:', statusData);
    } finally {
      setSaving(false);
    }
  };

  const updateService = (index: number, field: keyof ServiceStatus, value: string) => {
    const updatedServices = [...statusData.services];
    updatedServices[index] = { ...updatedServices[index], [field]: value };
    setStatusData({ ...statusData, services: updatedServices });
  };

  const addStatusUpdate = () => {
    if (!newUpdate.title || !newUpdate.description) return;
    
    const update: StatusUpdate = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      title: newUpdate.title,
      description: newUpdate.description
    };
    
    setStatusData({
      ...statusData,
      recent_updates: [update, ...statusData.recent_updates]
    });
    
    setNewUpdate({ title: '', description: '' });
  };

  const removeStatusUpdate = (id: string) => {
    setStatusData({
      ...statusData,
      recent_updates: statusData.recent_updates.filter(update => update.id !== id)
    });
  };

  if (loading) {
    return <div className="p-6">Loading system status...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Activity className="h-8 w-8" />
            System Status Management
          </h1>
          <p className="text-muted-foreground">
            Manage public system status page content
          </p>
        </div>
        <Button onClick={saveStatusData} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Metrics */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Metrics</CardTitle>
          <CardDescription>Update the metrics shown on the public status page</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm font-medium">Uptime %</label>
            <Input
              type="number"
              step="0.1"
              value={statusData.uptime_percentage}
              onChange={(e) => setStatusData({
                ...statusData,
                uptime_percentage: parseFloat(e.target.value) || 0
              })}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Response Time (ms)</label>
            <Input
              type="number"
              value={statusData.avg_response_time_ms}
              onChange={(e) => setStatusData({
                ...statusData,
                avg_response_time_ms: parseInt(e.target.value) || 0
              })}
            />
          </div>
          <div>
            <label className="text-sm font-medium">SMS Delivery %</label>
            <Input
              type="number"
              step="0.1"
              value={statusData.sms_delivery_rate}
              onChange={(e) => setStatusData({
                ...statusData,
                sms_delivery_rate: parseFloat(e.target.value) || 0
              })}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Active Incidents</label>
            <Input
              type="number"
              value={statusData.active_incidents}
              onChange={(e) => setStatusData({
                ...statusData,
                active_incidents: parseInt(e.target.value) || 0
              })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Services */}
      <Card>
        <CardHeader>
          <CardTitle>Service Status</CardTitle>
          <CardDescription>Update individual service statuses</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {statusData.services.map((service, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border rounded">
              <div>
                <label className="text-sm font-medium">Service Name</label>
                <Input
                  value={service.name}
                  onChange={(e) => updateService(index, 'name', e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={service.description}
                  onChange={(e) => updateService(index, 'description', e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Status</label>
                <select
                  className="w-full p-2 border rounded"
                  value={service.status}
                  onChange={(e) => updateService(index, 'status', e.target.value)}
                >
                  <option value="operational">Operational</option>
                  <option value="degraded">Degraded</option>
                  <option value="outage">Outage</option>
                </select>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Status Updates */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Updates</CardTitle>
          <CardDescription>Manage status updates shown on the public page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add New Update */}
          <div className="p-4 border rounded bg-gray-50">
            <h4 className="font-medium mb-3">Add New Update</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                placeholder="Update title"
                value={newUpdate.title}
                onChange={(e) => setNewUpdate({ ...newUpdate, title: e.target.value })}
              />
              <div className="md:col-span-2 flex gap-2">
                <Textarea
                  placeholder="Update description"
                  value={newUpdate.description}
                  onChange={(e) => setNewUpdate({ ...newUpdate, description: e.target.value })}
                  className="flex-1"
                />
                <Button onClick={addStatusUpdate}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Existing Updates */}
          {statusData.recent_updates.map((update) => (
            <div key={update.id} className="flex items-start justify-between p-4 border rounded">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium">{update.title}</span>
                  <Badge variant="outline">
                    {new Date(update.date).toLocaleDateString()}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{update.description}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeStatusUpdate(update.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}

          {statusData.recent_updates.length === 0 && (
            <p className="text-center text-muted-foreground py-8">
              No recent updates. Add one above to get started.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}